import { useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { License } from '../types/license';
import { User } from 'firebase/auth';

export const useLicense = (user: User | null) => {
    const [license, setLicense] = useState<License | null>(null);
    const [loading, setLoading] = useState(true);
    const [lastUserUid, setLastUserUid] = useState<string | null>(null);

    // Synchronously reset loading if user changed
    const currentUid = user?.uid ?? null;
    if (currentUid !== lastUserUid) {
        setLastUserUid(currentUid);
        setLoading(true);
    }

    useEffect(() => {
        if (!user) {
            setLicense(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        const docRef = doc(db, 'licenses', user.uid);

        const unsubscribe = onSnapshot(docRef, async (snapshot) => {
            if (snapshot.exists()) {
                setLicense({ ...snapshot.data() as License, id: snapshot.id });
                setLoading(false);
            } else {
                // CRITICAL FIX: Do NOT auto-create Trial here.
                // If network fails or cache is empty, we must NOT assume the user is new.
                // Doing so risks overwriting a valid Paid license with a Trial if the client thinks it doesn't exist due to sync/cache issues.
                // Instead, we just leave license as null (or set a specific status) and let the UI handle "No License Found".
                // The user can manually click "Start Trial" or "Restore Purchase" in the UI if they are truly new.
                console.warn("[useLicense] License document not found. Waiting for manual action or sync.");
                setLicense(null);
                setLoading(false);
            }
        }, (error) => {
            console.error("License listener error:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [user]);

    return { license, loading };
};
